import {
  CheckCircleIcon,
  ClockIcon,
  CogIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  PlayIcon,
  PlusIcon,
  TrashIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { apiService } from '../../services/api';

interface RunbookStep {
  id: string;
  title: string;
  description: string;
  command: string | null;
  expected_output: string | null;
  timeout_seconds: number | null;
  order: number;
  automated: boolean;
  runbook_id: string;
}

interface Runbook {
  id: string;
  title: string;
  description: string;
  category: string;
  tags: string[];
  estimated_duration: number | null;
  steps: RunbookStep[];
  created_at: string;
  updated_at: string;
  last_executed: string | null;
  execution_count: number;
  tenant_id: string;
}

interface RunbookExecution {
  id: string;
  runbook_id: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  started_at: string | null;
  completed_at: string | null;
  current_step: number;
  results: Record<string, unknown> | null;
  error_message: string | null;
}

const RunbookPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showRunbookForm, setShowRunbookForm] = useState(false);
  const [editingRunbook, setEditingRunbook] = useState<Runbook | null>(null);
  const [selectedRunbook, setSelectedRunbook] = useState<Runbook | null>(null);
  const [executingRunbook, setExecutingRunbook] = useState<string | null>(null);

  const queryClient = useQueryClient();

  const {
    data: runbooks,
    isLoading,
    error,
  } = useQuery<Runbook[]>({
    queryKey: ['tracker-runbooks', searchTerm, selectedCategory],
    queryFn: async (): Promise<Runbook[]> => {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (selectedCategory) params.append('category', selectedCategory);

      const response = await apiService.get(`/tracker/runbooks?${params.toString()}`);
      return (response as Runbook[]) || [];
    },
    refetchInterval: 30000,
  });

  const executeRunbookMutation = useMutation({
    mutationFn: async (runbookId: string): Promise<RunbookExecution> => {
      const response = await apiService.post(`/tracker/runbooks/${runbookId}/execute`, {});
      return response as RunbookExecution;
    },
    onSuccess: (data: RunbookExecution) => {
      setExecutingRunbook(data.id);
      queryClient.invalidateQueries({ queryKey: ['tracker-runbooks'] });
    },
  });

  const deleteRunbookMutation = useMutation({
    mutationFn: async (runbookId: string) => {
      return await apiService.delete(`/tracker/runbooks/${runbookId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tracker-runbooks'] });
    },
  });

  const { data: executionStatus } = useQuery<RunbookExecution | null>({
    queryKey: ['runbook-execution', executingRunbook],
    queryFn: async (): Promise<RunbookExecution | null> => {
      if (!executingRunbook) return null;
      const response = await apiService.get(`/tracker/executions/${executingRunbook}`);
      return response as RunbookExecution;
    },
    enabled: !!executingRunbook,
    refetchInterval: 5000,
  });

  const handleExecuteRunbook = (runbookId: string) => {
    executeRunbookMutation.mutate(runbookId);
  };

  const handleDeleteRunbook = (runbookId: string) => {
    if (window.confirm('Are you sure you want to delete this runbook?')) {
      deleteRunbookMutation.mutate(runbookId);
    }
  };

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return 'N/A';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const formatTimestamp = (timestamp: string | null) => {
    if (!timestamp) return 'Never';
    return new Date(timestamp).toLocaleString();
  };

  const getExecutionStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'text-yellow-600 bg-yellow-50';
      case 'running':
        return 'text-blue-600 bg-blue-50';
      case 'completed':
        return 'text-green-600 bg-green-50';
      case 'failed':
        return 'text-red-600 bg-red-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const filteredRunbooks = runbooks || [];
  const categories = Array.from(
    new Set(filteredRunbooks.map((runbook: Runbook) => runbook.category))
  );

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="h-48 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error loading runbooks</h3>
              <p className="mt-2 text-sm text-red-700">
                {error instanceof Error ? error.message : 'Unknown error'}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Runbooks</h1>
          <p className="text-gray-600">Operational procedures and automated workflows</p>
        </div>
        <button
          onClick={() => setShowRunbookForm(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Add Runbook
        </button>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search runbooks..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 w-full"
            />
          </div>
        </div>
        <div>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Categories</option>
            {categories.map((category: string) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
        </div>
        <div className="text-sm text-gray-600 flex items-center">
          Total: {filteredRunbooks.length} runbooks
        </div>
      </div>

      {/* Runbooks Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredRunbooks.map((runbook: Runbook) => (
          <div
            key={runbook.id}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-gray-900 truncate">{runbook.title}</h3>
                <p className="text-sm text-gray-600 mt-1 line-clamp-2">{runbook.description}</p>
              </div>
              <div className="flex items-center space-x-1 ml-2">
                <button
                  onClick={() => setSelectedRunbook(runbook)}
                  className="text-gray-400 hover:text-gray-600 p-1"
                  title="View Details"
                >
                  <DocumentTextIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setEditingRunbook(runbook)}
                  className="text-gray-400 hover:text-gray-600 p-1"
                  title="Edit Runbook"
                >
                  <PencilIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDeleteRunbook(runbook.id)}
                  className="text-gray-400 hover:text-red-600 p-1"
                  title="Delete Runbook"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="space-y-2 text-xs text-gray-500 mb-4">
              <div className="flex items-center">
                <InformationCircleIcon className="h-3 w-3 mr-1" />
                Category: {runbook.category}
              </div>
              <div className="flex items-center">
                <ClockIcon className="h-3 w-3 mr-1" />
                Duration: {formatDuration(runbook.estimated_duration)}
              </div>
              <div className="flex items-center">
                <PlayIcon className="h-3 w-3 mr-1" />
                Executed: {runbook.execution_count} times
              </div>
              <div className="flex items-center">
                <CheckCircleIcon className="h-3 w-3 mr-1" />
                Last run: {formatTimestamp(runbook.last_executed)}
              </div>
            </div>

            {/* Tags */}
            {runbook.tags && runbook.tags.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-4">
                {runbook.tags.map((tag: string, index: number) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}

            {/* Actions */}
            <div className="flex space-x-2">
              <button
                onClick={() => handleExecuteRunbook(runbook.id)}
                disabled={executeRunbookMutation.isLoading}
                className="flex-1 inline-flex justify-center items-center px-3 py-2 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
              >
                <PlayIcon className="h-3 w-3 mr-1" />
                {executeRunbookMutation.isLoading ? 'Starting...' : 'Execute'}
              </button>
              <button
                onClick={() => setSelectedRunbook(runbook)}
                className="flex-1 inline-flex justify-center items-center px-3 py-2 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
              >
                View Steps
              </button>
            </div>
          </div>
        ))}
      </div>

      {filteredRunbooks.length === 0 && (
        <div className="text-center py-12">
          <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No runbooks found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm || selectedCategory
              ? 'Try adjusting your filters.'
              : 'Start by creating your first runbook!'}
          </p>
        </div>
      )}

      {/* Execution Status */}
      {executionStatus && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Execution Status</h2>
            <div
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getExecutionStatusColor(executionStatus.status)}`}
            >
              {executionStatus.status}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-700">Started:</span>
              <span className="ml-2 text-gray-600">
                {formatTimestamp(executionStatus.started_at)}
              </span>
            </div>
            <div>
              <span className="font-medium text-gray-700">Current Step:</span>
              <span className="ml-2 text-gray-600">{executionStatus.current_step}</span>
            </div>
            <div>
              <span className="font-medium text-gray-700">Status:</span>
              <span
                className={`ml-2 capitalize ${executionStatus.status === 'completed' ? 'text-green-600' : executionStatus.status === 'failed' ? 'text-red-600' : 'text-blue-600'}`}
              >
                {executionStatus.status}
              </span>
            </div>
          </div>

          {executionStatus.error_message && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-800">{executionStatus.error_message}</p>
            </div>
          )}

          {executionStatus.status === 'completed' && (
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setExecutingRunbook(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          )}
        </div>
      )}

      {/* Runbook Details Modal */}
      {selectedRunbook && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white max-h-screen overflow-y-auto">
            <div className="mt-3">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">{selectedRunbook.title}</h3>
                  <p className="text-sm text-gray-600 mt-1">{selectedRunbook.description}</p>
                </div>
                <button
                  onClick={() => setSelectedRunbook(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-700">Category:</span>
                    <span className="ml-2 text-gray-600">{selectedRunbook.category}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Estimated Duration:</span>
                    <span className="ml-2 text-gray-600">
                      {formatDuration(selectedRunbook.estimated_duration)}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Executions:</span>
                    <span className="ml-2 text-gray-600">{selectedRunbook.execution_count}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Last Executed:</span>
                    <span className="ml-2 text-gray-600">
                      {formatTimestamp(selectedRunbook.last_executed)}
                    </span>
                  </div>
                </div>

                {/* Tags */}
                {selectedRunbook.tags && selectedRunbook.tags.length > 0 && (
                  <div>
                    <span className="font-medium text-gray-700">Tags:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {selectedRunbook.tags.map((tag: string, index: number) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Steps */}
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-3">
                    Steps ({selectedRunbook.steps.length})
                  </h4>
                  <div className="space-y-3">
                    {selectedRunbook.steps
                      .sort((a: RunbookStep, b: RunbookStep) => a.order - b.order)
                      .map((step: RunbookStep, index: number) => (
                        <div key={step.id} className="border border-gray-200 rounded-md p-4">
                          <div className="flex items-start space-x-3">
                            <div className="flex-shrink-0 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-medium">
                              {index + 1}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center space-x-2 mb-2">
                                <h5 className="text-sm font-medium text-gray-900">{step.title}</h5>
                                {step.automated && (
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    <CogIcon className="h-3 w-3 mr-1" />
                                    Automated
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-600 mb-2">{step.description}</p>

                              {step.command && (
                                <div className="mb-2">
                                  <span className="text-xs font-medium text-gray-700">
                                    Command:
                                  </span>
                                  <code className="ml-2 text-xs bg-gray-100 px-2 py-1 rounded">
                                    {step.command}
                                  </code>
                                </div>
                              )}

                              {step.expected_output && (
                                <div className="mb-2">
                                  <span className="text-xs font-medium text-gray-700">
                                    Expected Output:
                                  </span>
                                  <p className="ml-2 text-xs text-gray-600 bg-gray-50 p-2 rounded mt-1">
                                    {step.expected_output}
                                  </p>
                                </div>
                              )}

                              {step.timeout_seconds && (
                                <div className="text-xs text-gray-500">
                                  Timeout: {step.timeout_seconds} seconds
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => handleExecuteRunbook(selectedRunbook.id)}
                  disabled={executeRunbookMutation.isLoading}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 disabled:opacity-50"
                >
                  <PlayIcon className="h-4 w-4 mr-2" />
                  {executeRunbookMutation.isLoading ? 'Starting...' : 'Execute Runbook'}
                </button>
                <button
                  onClick={() => setSelectedRunbook(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Runbook Modal - Placeholder for now */}
      {showRunbookForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingRunbook ? 'Edit Runbook' : 'Add New Runbook'}
              </h3>
              <div className="text-center py-8">
                <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
                <p className="mt-2 text-sm text-gray-600">Runbook creation form coming soon...</p>
              </div>
              <div className="flex justify-end mt-6">
                <button
                  onClick={() => {
                    setShowRunbookForm(false);
                    setEditingRunbook(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RunbookPage;
